import { useState } from "react";
import { Upload, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useSite } from "../../context/SiteContext";

function fileToDataUrl(file, maxW) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxW / img.width);
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#fff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function TeamManager() {
  const { team, reload } = useSite();
  const [busy, setBusy] = useState(false);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        const photo = await fileToDataUrl(f, 700);
        await api.create("team", { photo, name: "New Member", role: "" });
      }
      toast.success(`${files.length} photo(s) added`);
      await reload();
    } catch { toast.error("Upload failed"); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const saveField = async (item, patch) => {
    try { await api.update("team", item.id, { photo: item.photo, name: item.name, role: item.role, ...patch }); await reload(); }
    catch { toast.error("Failed to save"); }
  };

  const remove = async (id) => {
    try { await api.remove("team", id); toast.success("Member removed"); await reload(); }
    catch { toast.error("Failed to delete"); }
  };

  return (
    <div data-testid="team-manager">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <h2 className="font-serif text-2xl text-forest">{team.length} Employee Photo{team.length !== 1 ? "s" : ""}</h2>
          <p className="text-muted text-sm mt-1">Round photos of faculty &amp; employees that scroll across the Home page. Add as many as you like.</p>
        </div>
        <label className="inline-flex items-center gap-2 bg-gold text-forest px-6 py-3 rounded-full text-sm uppercase tracking-widest hover:-translate-y-0.5 transition-transform cursor-pointer">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Add Photos
          <input data-testid="upload-team" type="file" accept="image/*" multiple onChange={onUpload} className="hidden" disabled={busy} />
        </label>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {team.map((m) => (
          <div key={m.id || m.photo} data-testid="team-admin-item" className="bg-white border border-line rounded-sm overflow-hidden">
            <div className="p-4 flex justify-center bg-bone">
              <div className="w-24 h-24 rounded-full overflow-hidden ring-2 ring-gold/40">
                <img src={m.photo} alt={m.name} className="w-full h-full object-cover" />
              </div>
            </div>
            <div className="p-3 space-y-2">
              <input
                data-testid="team-name-input"
                defaultValue={m.name}
                placeholder="Name"
                onBlur={(e) => e.target.value !== m.name && saveField(m, { name: e.target.value })}
                className="w-full text-sm font-serif bg-transparent border-b border-line focus:border-gold outline-none py-1"
              />
              <input
                data-testid="team-role-input"
                defaultValue={m.role}
                placeholder="Role / Department"
                onBlur={(e) => e.target.value !== m.role && saveField(m, { role: e.target.value })}
                className="w-full text-xs text-muted bg-transparent border-b border-line focus:border-gold outline-none py-1"
              />
              <button data-testid="delete-team-btn" onClick={() => remove(m.id)} className="mt-1 inline-flex items-center gap-1.5 text-xs text-destructive hover:underline">
                <Trash2 size={13} /> Remove
              </button>
            </div>
          </div>
        ))}
        {team.length === 0 && <p className="p-8 text-muted col-span-full">No team photos yet. Add some above.</p>}
      </div>
    </div>
  );
}
